import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    closeDetailPanel,
    setSelectedUser,
    toggleEditModal,
    setEditedUser,
    openMessageModal
} from '../../../store/usersSlice';
import StatusBadge from '../status_badge/StatusBadge.jsx';
import { ReactComponent as CloseIcon } from '../../icons/close.svg';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash.svg';
import { ReactComponent as LockIcon } from '../../icons/lock.svg';
import { ReactComponent as UnlockIcon } from '../../icons/unlock.svg';
import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import peopleIcon from '../../icons/people.png';
import profileAddIcon from '../../icons/profile-add.png';
import profileTickIcon from '../../icons/profile-tick.png';
import magicStarIcon from '../../icons/magic-star.png';
import { ReactComponent as CalendarIcon } from '../../icons/calendar.svg';
import { ReactComponent as ClockIcon } from '../../icons/clock.svg';
import { ReactComponent as SearchIcon } from '../../icons/search.svg';
import { ReactComponent as ArrowUpDownIcon } from '../../icons/arrow-up-down.svg';
import { ReactComponent as MoreIcon } from '../../icons/dots.svg';
import Row from '../../list2/row';
import ApiService from '../../api_services/apiService.jsx';
import './UserDetailPanel.css';
import '../event_detail_panel/EventDetailPanel.css';

const normalizeListResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.content)) return response.content;
    return [];
};

const formatDateTimeValue = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    return date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).replace(/\//g, '-');
};

const formatTimeValue = (timeString) => {
    if (!timeString) return '-';
    return String(timeString).slice(0, 5);
};

const formatLongDateValue = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    return date.toLocaleDateString('pl-PL', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

const normalizeProfileImageHash = (value) => {
    if (value === null || value === undefined) return null;

    const text = String(value).trim();
    const lowerText = text.toLowerCase();
    if (!text || text === '_PROFILE_' || lowerText === 'null' || lowerText === '_profile_null') return null;
    if (text.startsWith('_PROFILE_')) return text;
    return `_PROFILE_${text.replace(/^_PROFILE_/, '')}`;
};

const normalizeProfileImageUrl = (value) => {
    if (value === null || value === undefined) return null;

    const text = String(value).trim();
    if (!text || text.toLowerCase() === 'null') return null;
    return text;
};

const getRawProfile = (user) => (
    user?._rawProfileEntry?.profileResponseDto
    || user?._rawProfileEntry?.profile
    || user?._rawProfileEntry?.profileDto
    || user?._rawProfileEntry
    || {}
);

const resolveProfileImageHash = (user, loadedProfile = null) => {
    const rawProfile = getRawProfile(user);

    return normalizeProfileImageHash(
        loadedProfile?.profileImageHashedId
        || user?.profileImageHashedId
        || user?.profileImageHash
        || user?.avatarHash
        || rawProfile?.profileImageHashedId
        || rawProfile?.profileImageHash
        || rawProfile?.avatarHash
    );
};

const resolveProfileImageUrl = (user, loadedProfile = null) => {
    const rawProfile = user?._rawProfileEntry?.profileResponseDto
        || user?._rawProfileEntry?.profile
        || user?._rawProfileEntry?.profileDto
        || user?._rawProfileEntry
        || {};

    return normalizeProfileImageUrl(
        loadedProfile?.profileImageUrl
        || loadedProfile?.imageUrl
        || user?.profileImageUrl
        || user?.imageUrl
        || rawProfile?.profileImageUrl
        || rawProfile?.imageUrl
    );
};

const mapBunchToPackage = (bunch, selectedUser) => {
    const participants = Array.isArray(bunch?.participants) ? bunch.participants : [];
    return {
        id: bunch?.id ?? '-',
        eventId: bunch?.eventId ?? null,
        name: bunch?.name || `Paczka #${bunch?.id ?? '-'}`,
        founder: selectedUser?.nickname || selectedUser?.fullName || selectedUser?.id || '-',
        memberCount: participants.length,
        description: bunch?.description || 'Brak opisu paczki.',
        meetingDate: bunch?.meetingDate || bunch?.addedOn || null,
        meetingTime: bunch?.meetingTime || null,
        isTimeFixed: bunch?.isTimeFixed === true,
        isPublic: bunch?.isPublic === true,
        numberOfEmptySlots: bunch?.numberOfEmptySlots,
        addedOn: bunch?.addedOn || null,
        event: {
            name: bunch?.eventId ? `Wydarzenie #${bunch.eventId}` : '-',
            location: '-',
            date: bunch?.meetingDate || bunch?.addedOn || null,
            time: bunch?.meetingTime || null,
        },
        users: participants.map((participant, index) => ({
            id: participant?.userId || index + 1,
            username: participant?.userId || `Uczestnik #${index + 1}`,
            isFounder: participant?.typeOfParticipant === 'ADMIN',
        })),
        raw: bunch,
    };
};

const resolveProfileDisplayName = (profile, fallback = '-') => {
    if (!profile) return fallback;

    const fullName = [profile?.name, profile?.surname].filter(Boolean).join(' ').trim();
    return profile?.nick || fullName || profile?.email || fallback;
};

const mapUserReport = (report, profilesById = new Map()) => {
    const reportSenderId = report?.reportSenderId || '';
    const reporterProfile = profilesById.get(reportSenderId);

    return {
        id: report?.reportId ?? report?.id,
        reportId: report?.reportId ?? report?.id,
        date: formatDateTimeValue(report?.createdAt),
        reporter: resolveProfileDisplayName(reporterProfile, reportSenderId || '-'),
        packName: '-',
        reason: report?.reason || '-',
        reportedUserId: report?.reportedUserId || '-',
        reportSenderId: reportSenderId || '-',
        phoneNumber: report?.phoneNumber || '-',
        email: report?.email || '-',
        sex: report?.sex || '-',
    };
};

function UserDetailPanel({ allUsers = [], sourceTab = 'users' }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const selectedUser = useSelector((state) => state.users.selectedUser);
    const detailPanelOpen = useSelector((state) => state.users.detailPanelOpen);
    const accessToken = useSelector((state) => state.tokens.accessToken);
    const [activeTab, setActiveTab] = useState('basic');
    const shouldLoadUserAdminSections = sourceTab === 'users' || sourceTab === 'admins';

    const hasOrganizer = Boolean(
        selectedUser?.hasOrganizerProfile ||
        selectedUser?.role === 'organizer' ||
        selectedUser?.organiserProfileId ||
        selectedUser?.organizerProfileId ||
        selectedUser?._rawProfileEntry?.organiserProfileResponseDto?.id ||
        selectedUser?._rawProfileEntry?.organiserProfileResponseDto?.companyName ||
        selectedUser?._rawProfileEntry?.organiserProfileResponseDto?.contactEmail
    );

    const handleNavigateToOrganizer = useCallback(() => {
        const organizerId =
            selectedUser?.organiserProfileId ||
            selectedUser?.organizerProfileId ||
            selectedUser?._rawProfileEntry?.organiserProfileResponseDto?.id ||
            selectedUser?._rawProfileEntry?.organiserProfileResponseDto?.organiserProfileId ||
            selectedUser?.id;

        if (!organizerId) return;

        dispatch(closeDetailPanel());
        navigate(`/organizers?organizerId=${encodeURIComponent(organizerId)}`, {
            state: { organizerId, selectedOrganizerId: organizerId },
        });
    }, [selectedUser, dispatch, navigate]);

    useEffect(() => {
        if (!shouldLoadUserAdminSections && (activeTab === 'packs' || activeTab === 'reports')) {
            setActiveTab('basic');
        }
    }, [activeTab, shouldLoadUserAdminSections]);

    const statusValue = selectedUser?.status;
    const statusLabel = statusValue === 'blocked'
        ? 'Zablokowany'
        : statusValue === 'pending'
            ? 'Oczekujące'
            : 'Aktywny';
    const statusType = statusValue === 'blocked'
        ? 'inactive'
        : statusValue === 'pending'
            ? 'pending'
            : 'active';

    const nameParts = useMemo(() => {
        const fullName = selectedUser?.fullName || '';
        const parts = fullName.split(' ');
        const firstName = parts[0] || '-';
        const lastName = parts.slice(1).join(' ') || '-';
        return { firstName, lastName };
    }, [selectedUser?.fullName]);

    const currentIndex = selectedUser
        ? allUsers.findIndex((u) => u.id === selectedUser.id)
        : -1;
    const totalCount = allUsers.length;

    const handlePrevious = useCallback(() => {
        if (currentIndex > 0) {
            dispatch(setSelectedUser(allUsers[currentIndex - 1]));
        }
    }, [currentIndex, allUsers, dispatch]);

    const handleNext = useCallback(() => {
        if (currentIndex >= 0 && currentIndex < allUsers.length - 1) {
            dispatch(setSelectedUser(allUsers[currentIndex + 1]));
        }
    }, [currentIndex, allUsers, dispatch]);

    const handleEdit = useCallback(() => {
        if (selectedUser) {
            dispatch(setEditedUser(selectedUser));
            dispatch(toggleEditModal());
        }
    }, [selectedUser, dispatch]);

    const handleBlock = useCallback(async () => {
        const userId = selectedUser?.profileUuid || selectedUser?.reportedUserId || selectedUser?.id;
        if (!userId) {
            window.alert('Brak identyfikatora użytkownika.');
            return;
        }

        const reason = window.prompt('Podaj powód blokady:');
        if (reason === null) return;

        const normalizedReason = reason.trim();
        if (!normalizedReason) {
            window.alert('Powód blokady jest wymagany.');
            return;
        }

        try {
            await ApiService.putBan({
                id: userId,
                reason: normalizedReason,
            });
            window.alert('Użytkownik został zablokowany.');
            window.location.reload();
        } catch (error) {
            console.error('Nie udało się zablokować użytkownika:', error);
            window.alert('Nie udało się zablokować użytkownika.');
        }
    }, [selectedUser]);

    const handleUnblock = useCallback(async () => {
        const userId = selectedUser?.profileUuid || selectedUser?.reportedUserId || selectedUser?.id;
        if (!userId) {
            window.alert('Brak identyfikatora użytkownika.');
            return;
        }

        try {
            await ApiService.unblockUser(userId);
            window.alert('Użytkownik został odblokowany.');
            window.location.reload();
        } catch (error) {
            console.error('Nie udało się odblokować użytkownika:', error);
            window.alert('Nie udało się odblokować użytkownika.');
        }
    }, [selectedUser]);

    const [expandedPackageId, setExpandedPackageId] = useState(null);
    const [selectedPackageForDetails, setSelectedPackageForDetails] = useState(null);
    const [packagesSearch, setPackagesSearch] = useState('');
    const [packagesSort, setPackagesSort] = useState({ field: 'addedOn', direction: 'desc' });
    const [hoveredPackageId, setHoveredPackageId] = useState(null);
    const [expandedReportId, setExpandedReportId] = useState(null);
    const [packages, setPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(false);
    const [packagesError, setPackagesError] = useState('');
    const [reportsData, setReportsData] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [reportsError, setReportsError] = useState('');
    const [userStats, setUserStats] = useState(null);
    const [userStatsLoading, setUserStatsLoading] = useState(false);
    const [userStatsError, setUserStatsError] = useState('');
    const [selectedUserProfile, setSelectedUserProfile] = useState(null);
    const [profileImageSrc, setProfileImageSrc] = useState('');
    const [profileImageError, setProfileImageError] = useState(false);

    const selectedProfileId = selectedUser?.profileUuid || selectedUser?.reportedUserId || selectedUser?.id || null;
    const profileImageUrl = useMemo(
        () => resolveProfileImageUrl(selectedUser, selectedUserProfile),
        [selectedUser, selectedUserProfile]
    );
    const profileImageHash = useMemo(
        () => resolveProfileImageHash(selectedUser, selectedUserProfile),
        [selectedUser, selectedUserProfile]
    );

    useEffect(() => {
        setProfileImageError(false);
        setProfileImageSrc('');
    }, [selectedProfileId, profileImageUrl, profileImageHash]);

    useEffect(() => {
        if (!detailPanelOpen || !selectedProfileId) {
            setSelectedUserProfile(null);
            return undefined;
        }

        const controller = new AbortController();

        const fetchSelectedUserProfile = async () => {
            try {
                const profile = await ApiService.getProfileById(selectedProfileId, { signal: controller.signal });
                if (!controller.signal.aborted) {
                    setSelectedUserProfile(profile?.data ?? profile ?? null);
                }
            } catch (error) {
                if (error?.code !== 'ERR_CANCELED') {
                    console.error('Błąd pobierania profilu użytkownika:', error);
                }
                if (!controller.signal.aborted) {
                    setSelectedUserProfile(null);
                }
            }
        };

        fetchSelectedUserProfile();

        return () => {
            controller.abort();
        };
    }, [detailPanelOpen, selectedProfileId]);

    useEffect(() => {
        let isMounted = true;
        let objectUrl = null;

        const loadProfileImage = async () => {
            const resolvedImageUrl = profileImageUrl || (profileImageHash ? `/open/images/${profileImageHash}` : null);

            if (!resolvedImageUrl) {
                if (isMounted) {
                    setProfileImageError(true);
                    setProfileImageSrc('');
                }
                return;
            }

            try {
                const response = await ApiService.get(resolvedImageUrl, {
                    responseType: 'blob',
                    headers: {
                        Accept: 'image/*',
                    },
                });

                if (isMounted) {
                    objectUrl = URL.createObjectURL(response.data);
                    setProfileImageSrc(objectUrl);
                    setProfileImageError(false);
                }
            } catch (error) {
                console.error('Błąd pobierania zdjęcia profilowego:', error);
                if (isMounted) {
                    setProfileImageSrc('');
                    setProfileImageError(true);
                }
            }
        };

        loadProfileImage();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [profileImageHash, profileImageUrl, accessToken]);

    useEffect(() => {
        if (!detailPanelOpen || !selectedUser?.id || !shouldLoadUserAdminSections) {
            setPackages([]);
            setReportsData([]);
            setUserStats(null);
            return undefined;
        }

        const controller = new AbortController();

        const fetchUserDetailSections = async () => {
            setPackagesLoading(true);
            setReportsLoading(true);
            setUserStatsLoading(true);
            setPackagesError('');
            setReportsError('');
            setUserStatsError('');

            try {
                const [bunchesResult, reportsResult, statsResult] = await Promise.allSettled([
                    ApiService.getBunchesCreatedByUser(selectedUser.id, { signal: controller.signal }),
                    ApiService.getUserReportsByReportedUser(selectedUser.id, { signal: controller.signal }),
                    ApiService.getProfileStats(selectedUser.id, { signal: controller.signal }),
                ]);

                if (controller.signal.aborted) return;

                if (bunchesResult.status === 'fulfilled') {
                    setPackages(normalizeListResponse(bunchesResult.value).map((item) => mapBunchToPackage(item, selectedUser)));
                } else {
                    console.error('Error fetching user created bunches:', bunchesResult.reason);
                    setPackages([]);
                    setPackagesError('Nie udało się pobrać utworzonych paczek.');
                }

                if (statsResult.status === 'fulfilled') {
                    setUserStats(statsResult.value?.data ?? statsResult.value ?? null);
                } else {
                    console.error('Error fetching user stats:', statsResult.reason);
                    setUserStats(null);
                    setUserStatsError('Nie udało się pobrać statystyk użytkownika.');
                }

                if (reportsResult.status === 'fulfilled') {
                    const reports = normalizeListResponse(reportsResult.value);
                    const reportSenderIds = [...new Set(reports.map((report) => report?.reportSenderId).filter(Boolean))];
                    const profileResults = await Promise.allSettled(
                        reportSenderIds.map(async (userId) => ({
                            userId,
                            profile: await ApiService.getProfileById(userId, { signal: controller.signal }),
                        }))
                    );

                    if (controller.signal.aborted) return;

                    const profilesById = new Map();
                    profileResults.forEach((result) => {
                        if (result.status === 'fulfilled') {
                            profilesById.set(result.value.userId, result.value.profile);
                        }
                    });

                    setReportsData(reports.map((report) => mapUserReport(report, profilesById)));
                } else {
                    console.error('Error fetching user reports:', reportsResult.reason);
                    setReportsData([]);
                    setReportsError('Nie udało się pobrać zgłoszeń użytkownika.');
                }
            } finally {
                if (!controller.signal.aborted) {
                    setPackagesLoading(false);
                    setReportsLoading(false);
                    setUserStatsLoading(false);
                }
            }
        };

        fetchUserDetailSections();

        return () => {
            controller.abort();
        };
    }, [detailPanelOpen, selectedUser, shouldLoadUserAdminSections]);

    const filteredPackages = packages.filter((pkg) =>
        String(pkg.name || '').toLowerCase().includes(packagesSearch.toLowerCase())
        || String(pkg.founder || '').toLowerCase().includes(packagesSearch.toLowerCase())
    );

    const sortedPackages = [...filteredPackages].sort((a, b) => {
        const field = packagesSort.field;
        const direction = packagesSort.direction === 'asc' ? 1 : -1;
        const left = a?.[field];
        const right = b?.[field];
        if (left == null && right == null) return 0;
        if (left == null) return 1;
        if (right == null) return -1;
        if (typeof left === 'number' && typeof right === 'number') {
            return (left - right) * direction;
        }
        const leftTime = Date.parse(left);
        const rightTime = Date.parse(right);
        if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) {
            return (leftTime - rightTime) * direction;
        }
        return String(left).localeCompare(String(right), 'pl') * direction;
    });

    const handlePackageSort = (field) => {
        setPackagesSort((prev) => (
            prev.field === field
                ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { field, direction: field === 'addedOn' ? 'desc' : 'asc' }
        ));
    };

    const reportsHeaders = [
        <div key="reports-date-header">Data</div>,
        <div key="reports-from-header">Zgłoszenie od</div>,
        <div key="reports-pack-header">Nazwa paczki</div>,
        <div key="reports-reason-header">Powód</div>,
        <div key="reports-actions-header">Akcje</div>,
    ];

    const reportsColumnWidths = ['120px', '1.2fr', '1fr', '1fr', '180px'];
    const renderReportRow = (report) => ([
        report.date,
        report.reporter,
        report.packName,
        report.reason,
        <div key={`reports-actions-${report.id}`} className="actions-cell users-actions">
            <button className="report-reject-btn" type="button">
                Odrzuć
            </button>
            <button
                className="open-details-btn report-toggle-btn"
                type="button"
                aria-label="Szczegoly"
                aria-expanded={expandedReportId === report.id}
                aria-controls={`report-details-${report.id}`}
                onClick={() => setExpandedReportId(expandedReportId === report.id ? null : report.id)}
            >
                <ArrowRightIcon className="arrow-right-icon" />
            </button>
        </div>,
    ]);

    if (!detailPanelOpen || !selectedUser) return null;

    return (
        <>
            <div className="user-detail-backdrop" onClick={() => dispatch(closeDetailPanel())} />
            <div className="user-detail-panel">
                <div className="user-detail-header">
                    <div className="header-left">
                        <button
                            className="close-btn"
                            onClick={() => dispatch(closeDetailPanel())}
                            aria-label="Zamknij"
                            type="button"
                        >
                            <CloseIcon />
                        </button>
                        <div className="navigation-arrows">
                            <button
                                className="nav-arrow-btn"
                                onClick={handlePrevious}
                                disabled={currentIndex <= 0}
                                title="Poprzedni"
                                type="button"
                            >
                                <ArrowLeftIcon className="nav-arrow-icon" />
                            </button>
                            <button
                                className="nav-arrow-btn"
                                onClick={handleNext}
                                disabled={currentIndex >= totalCount - 1}
                                title="Następny"
                                type="button"
                            >
                                <ArrowRightIcon className="nav-arrow-icon" />
                            </button>
                        </div>
                        <span className="results-counter">
                            {currentIndex + 1} z {totalCount} wyników
                        </span>
                    </div>
                    {hasOrganizer && (
                        <div className="header-center">
                            <button
                                className="organizer-profile-nav-btn"
                                type="button"
                                onClick={handleNavigateToOrganizer}
                                title="Przejdź do profilu organizatora"
                            >
                                Profil organizatora
                            </button>
                        </div>
                    )}
                    <div className="header-right">
                        <span className="registration-date">
                            Data rejestracji: {selectedUser.registrationDate}
                        </span>
                    </div>
                </div>

                <div className="user-detail-content">
                    <div className="user-title-row">
                        <div className="user-title-left">
                            <span className="user-id-line">
                                (ID {selectedUser.id}) Ostatnie logowanie: {selectedUser.lastLogin || '-'}
                            </span>
                            <div className="user-name-row">
                                <h2 className="user-name">
                                    {selectedUser.nickname}
                                </h2>
                                <span className="user-fullname">
                                    ({selectedUser.fullName})
                                </span>
                                <StatusBadge status={statusType} label={statusLabel} />
                            </div>
                        </div>
                        <button
                            className="message-btn"
                            type="button"
                            onClick={() => dispatch(openMessageModal(selectedUser))}
                        >
                            Wyślij wiadomość
                        </button>
                    </div>

                    <div className="detail-tabs-nav">
                        <div className="tabs-container">
                            <button
                                className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
                                onClick={() => setActiveTab('basic')}
                                type="button"
                            >
                                Dane podstawowe
                            </button>
                            {shouldLoadUserAdminSections && (
                                <button
                                    className={`tab-btn ${activeTab === 'packs' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('packs')}
                                    type="button"
                                >
                                    Utworzone paczki
                                </button>
                            )}
                            <button
                                className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
                                onClick={() => setActiveTab('stats')}
                                type="button"
                            >
                                Statystyki
                            </button>
                            {shouldLoadUserAdminSections && (
                                <button
                                    className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('reports')}
                                    type="button"
                                >
                                    Zgłoszone zablokowania
                                    <span className="tab-badge">{reportsData.length}</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {activeTab === 'basic' && (
                        <div className="tab-content basic-data">
                            <div className="data-section">
                                <div className="data-row two-column">
                                    <div className="data-row-inner">
                                        <div className="data-label">ID</div>
                                        <div className="data-value">{selectedUser.id}</div>
                                    </div>
                                    <div className="data-row-inner">
                                        <div className="data-label">Nick</div>
                                        <div className="data-value">{selectedUser.nickname}</div>
                                    </div>
                                </div>
                                <div className="data-row two-column">
                                    <div className="data-row-inner">
                                        <div className="data-label">Imię</div>
                                        <div className="data-value">{nameParts.firstName}</div>
                                    </div>
                                    <div className="data-row-inner">
                                        <div className="data-label">Nazwisko</div>
                                        <div className="data-value">{nameParts.lastName}</div>
                                    </div>
                                </div>
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Płeć</div>
                                        <div className="data-value">{selectedUser.gender}</div>
                                    </div>
                                </div>
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Numer telefonu</div>
                                        <div className="data-value">{selectedUser.phone}</div>
                                    </div>
                                </div>
                                <div className="data-row contact-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Adres e-mail</div>
                                        <div className="data-value">{selectedUser.email}</div>
                                    </div>
                                    <button className="reset-btn" type="button">
                                        Wiadomość z resetem hasła
                                    </button>
                                </div>
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Opis</div>
                                        <div className="data-value">
                                            {selectedUser.description || 'Opis profilu...'}
                                        </div>
                                    </div>
                                </div>
                                <div className="data-row profile-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Zdjęcie profilowe</div>
                                        <div className="profile-image-frame">
                                            {profileImageSrc && !profileImageError ? (
                                                <img
                                                    className="profile-image"
                                                    src={profileImageSrc}
                                                    alt={`Zdjęcie profilowe ${selectedUser.nickname || selectedUser.fullName || selectedUser.id || 'użytkownika'}`}
                                                    onError={() => setProfileImageError(true)}
                                                />
                                            ) : (
                                                <div className="profile-placeholder">
                                                    <span className="profile-placeholder-initial">
                                                        {String(selectedUser.nickname || selectedUser.fullName || selectedUser.email || selectedUser.id || '-')
                                                            .charAt(0)
                                                            .toUpperCase()}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <button className="report-btn" type="button">
                                        Zgłoś naruszenie i usuń
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'packs' && (
                        <div className="tab-content packs-tab detail-tab-packs">
                            <div className="packs-header">
                                <span className="packs-count">Liczba paczek: {packages.length}</span>
                                <div className="packs-search">
                                    <SearchIcon className="packs-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Szukaj"
                                        value={packagesSearch}
                                        onChange={(e) => setPackagesSearch(e.target.value)}
                                        className="packs-search-input"
                                    />
                                </div>
                            </div>

                            <div className="packs-table-container">
                                <div className="packs-table">
                                    <div className="packs-table-header">
                                        <div className="packs-th packs-th-id" onClick={() => handlePackageSort('id')}>
                                            <span>ID</span>
                                            <ArrowUpDownIcon className={`packs-sort-icon ${packagesSort.field === 'id' ? 'active' : ''}`} />
                                        </div>
                                        <div className="packs-th packs-th-name" onClick={() => handlePackageSort('name')}>
                                            <span>Nazwa paczki</span>
                                            <ArrowUpDownIcon className={`packs-sort-icon ${packagesSort.field === 'name' ? 'active' : ''}`} />
                                        </div>
                                        <div className="packs-th packs-th-founder" onClick={() => handlePackageSort('founder')}>
                                            <span>Założyciel</span>
                                            <ArrowUpDownIcon className={`packs-sort-icon ${packagesSort.field === 'founder' ? 'active' : ''}`} />
                                        </div>
                                        <div className="packs-th packs-th-members" onClick={() => handlePackageSort('memberCount')}>
                                            <span>Liczba osób</span>
                                            <ArrowUpDownIcon className={`packs-sort-icon ${packagesSort.field === 'memberCount' ? 'active' : ''}`} />
                                        </div>
                                        <div className="packs-th packs-th-actions">
                                            <span>Akcje</span>
                                        </div>
                                    </div>

                                    <div className="packs-table-body">
                                        {packagesLoading ? (
                                            <div className="packs-empty-row"><p>Ładowanie paczek...</p></div>
                                        ) : packagesError ? (
                                            <div className="packs-empty-row"><p>{packagesError}</p></div>
                                        ) : sortedPackages.length > 0 ? (
                                            sortedPackages.map((pkg) => (
                                                <React.Fragment key={pkg.id}>
                                                    <div
                                                        className={`packs-table-row ${hoveredPackageId === pkg.id ? 'hovered' : ''} ${expandedPackageId === pkg.id ? 'expanded' : ''}`}
                                                        onMouseEnter={() => setHoveredPackageId(pkg.id)}
                                                        onMouseLeave={() => setHoveredPackageId(null)}
                                                        onClick={() => setExpandedPackageId(expandedPackageId === pkg.id ? null : pkg.id)}
                                                    >
                                                        <div className="packs-td packs-td-id">{pkg.id}</div>
                                                        <div className="packs-td packs-td-name">
                                                            <span className="packs-name-text">{pkg.name}</span>
                                                        </div>
                                                        <div className="packs-td packs-td-founder">{pkg.founder}</div>
                                                        <div className="packs-td packs-td-members">{pkg.memberCount}</div>
                                                        <div className="packs-td packs-td-actions">
                                                            {hoveredPackageId === pkg.id && (
                                                                <button
                                                                    className="packs-action-btn"
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedPackageForDetails(pkg);
                                                                    }}
                                                                >
                                                                    <MoreIcon />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {expandedPackageId === pkg.id && pkg.users && (
                                                        pkg.users.map((user, userIndex) => (
                                                            <div
                                                                key={`user-${pkg.id}-${user.id}`}
                                                                className={`packs-user-row ${userIndex === pkg.users.length - 1 ? 'last-user' : ''}`}
                                                            >
                                                                <div className="packs-user-td packs-user-spacer"></div>
                                                                <div className="packs-user-td packs-user-number">{user.id}</div>
                                                                <div className="packs-user-td packs-user-avatar">
                                                                    <div className="packs-avatar-circle">
                                                                        <span className="packs-avatar-placeholder">
                                                                            {String(user.username || '-').charAt(0).toUpperCase()}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="packs-user-td packs-user-name">
                                                                    <span className={user.isFounder ? 'founder-name' : ''}>
                                                                        {user.username}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <div className="packs-empty-row">
                                                <p>Brak paczek dla tego użytkownika</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'stats' && (
                        <div className="tab-content stats-tab">
                            <div className="stats-grid">
                                <div className="stats-card stats-card-peach">
                                    <div className="stats-metric">
                                        <img className="stats-icon-image" src={peopleIcon} alt="" />
                                        <div className="stats-value">
                                            {userStatsLoading
                                                ? '-'
                                                : (userStats?.foundedBunches ?? (packagesLoading ? '-' : packages.length))}
                                        </div>
                                    </div>
                                    <div className="stats-label">Założone paczki</div>
                                </div>
                                <div className="stats-card stats-card-teal">
                                    <div className="stats-metric">
                                        <img className="stats-icon-image" src={profileAddIcon} alt="" />
                                        <div className="stats-value">
                                            {userStatsLoading ? '-' : (userStats?.participatedBunches ?? '-')}
                                        </div>
                                    </div>
                                    <div className="stats-label">Dołączenia do paczek</div>
                                </div>
                                <div className="stats-card stats-card-teal">
                                    <div className="stats-metric">
                                        <img className="stats-icon-image" src={profileTickIcon} alt="" />
                                        <div className="stats-value">
                                            {userStatsLoading ? '-' : (userStats?.confirmedAttendance ?? '-')}
                                        </div>
                                    </div>
                                    <div className="stats-label">Potwierdzone obecności</div>
                                </div>
                                <div className="stats-card stats-card-peach">
                                    <div className="stats-metric">
                                        <img className="stats-icon-image" src={magicStarIcon} alt="" />
                                        <div className="stats-value">
                                            {userStatsLoading ? '-' : (userStats?.positiveReviews ?? '-')}
                                        </div>
                                    </div>
                                    <div className="stats-label">Pozytywne oceny od innych</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'reports' && (
                        <div className="tab-content reports-tab">
                            <div className="user-detail-table-scope table-container">
                                <div className="table">
                                    <div className="table-header">
                                        <Row columns={reportsHeaders} columnWidths={reportsColumnWidths} />
                                    </div>
                                    <div className="table-body">
                                        {reportsLoading ? (
                                            <div className="user-detail-empty-state">Ładowanie zgłoszeń...</div>
                                        ) : reportsError ? (
                                            <div className="user-detail-empty-state">{reportsError}</div>
                                        ) : reportsData.length === 0 ? (
                                            <div className="user-detail-empty-state">Brak zgłoszeń dla tego użytkownika</div>
                                        ) : reportsData.map((report) => (
                                            <React.Fragment key={report.id}>
                                                <Row
                                                    columns={renderReportRow(report)}
                                                    columnWidths={reportsColumnWidths}
                                                />
                                                {expandedReportId === report.id && (
                                                    <Row
                                                        rowClass="report-details-row"
                                                        columns={[
                                                            <div
                                                                key={`report-details-${report.id}`}
                                                                className="report-details-panel"
                                                                id={`report-details-${report.id}`}
                                                            >
                                                                <div className="report-details-grid">
                                                                    <div className="report-message-card">
                                                                        <div className="report-card-title">Zgłoszony użytkownik</div>
                                                                        <p className="report-message-text">ID: {report.reportedUserId}</p>
                                                                        <p className="report-message-text">Email: {report.email}</p>
                                                                        <p className="report-message-text">Telefon: {report.phoneNumber}</p>
                                                                        <p className="report-message-text">Płeć: {report.sex}</p>
                                                                    </div>
                                                                    <div className="report-message-card">
                                                                        <div className="report-card-title">Zgłoszenie</div>
                                                                        <p className="report-message-text">ID zgłoszenia: {report.reportId}</p>
                                                                        <p className="report-message-text">Zgłoszenie od: {report.reporter}</p>
                                                                        <p className="report-message-text">UUID zgłaszającego: {report.reportSenderId}</p>
                                                                        <p className="report-message-text">Data: {report.date}</p>
                                                                        <p className="report-message-text">Powód: {report.reason}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="report-details-actions">
                                                                    <button className="report-reject-btn" type="button">
                                                                        Odrzuć zgłoszenie
                                                                    </button>
                                                                </div>
                                                            </div>,
                                                            '',
                                                            '',
                                                            '',
                                                            '',
                                                        ]}
                                                        columnWidths={reportsColumnWidths}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="user-detail-footer">
                    <div className="footer-buttons">
                        <button className="action-btn outline danger" type="button">
                            <TrashIcon className="btn-icon" />
                            <span>Usuń</span>
                        </button>
                        <button
                            className="action-btn outline"
                            type="button"
                            onClick={selectedUser.status === 'blocked' ? handleUnblock : handleBlock}
                            title={selectedUser.status === 'blocked' ? 'Odblokuj użytkownika' : 'Zablokuj użytkownika'}
                        >
                            {selectedUser.status === 'blocked' ? (
                                <>
                                    <UnlockIcon className="btn-icon" />
                                    <span>Odblokuj</span>
                                </>
                            ) : (
                                <>
                                    <LockIcon className="btn-icon" />
                                    <span>Zablokuj</span>
                                </>
                            )}
                        </button>
                        <button className="action-btn filled" type="button" onClick={handleEdit}>
                            <EditIcon className="btn-icon" />
                            <span>Edytuj</span>
                        </button>
                    </div>
                </div>
            </div>

            {selectedPackageForDetails && (
                <div className="package-modal-overlay" onClick={() => setSelectedPackageForDetails(null)}>
                    <div className="package-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="package-modal-header">
                            <button
                                className="package-modal-close"
                                onClick={() => setSelectedPackageForDetails(null)}
                            >
                                <CloseIcon />
                            </button>
                        </div>

                        <div className="package-modal-title-section">
                            <div className="package-modal-title-info">
                                <h2 className="package-modal-title">{selectedPackageForDetails.name}</h2>
                                <span className="package-modal-date">
                                    {formatLongDateValue(selectedPackageForDetails.meetingDate || selectedPackageForDetails.event.date)}
                                </span>
                            </div>
                        </div>

                        <div className="package-modal-section">
                            <h3 className="package-modal-section-title">Wydarzenie</h3>
                            <div className="package-modal-event-card">
                                <div className="package-event-image">
                                    <div className="package-event-image-placeholder">
                                        <span>{String(selectedPackageForDetails.event.name || '-').charAt(0)}</span>
                                    </div>
                                </div>
                                <div className="package-event-details">
                                    <div className="package-event-info">
                                        <span className="package-event-name">{selectedPackageForDetails.event.name}</span>
                                        <span className="package-event-location">{selectedPackageForDetails.event.location}</span>
                                    </div>
                                    <div className="package-event-datetime">
                                        <span className="package-event-date-small">
                                            {formatLongDateValue(selectedPackageForDetails.event.date)}
                                        </span>
                                        <span className="package-event-time">{formatTimeValue(selectedPackageForDetails.event.time)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="package-modal-section">
                            <h3 className="package-modal-section-title">Data i godzina wyjścia</h3>
                            <div className="package-modal-datetime-row">
                                <div className="package-datetime-item">
                                    <CalendarIcon className="package-datetime-icon" />
                                    <span className="package-datetime-value">
                                        {formatDateTimeValue(selectedPackageForDetails.meetingDate || selectedPackageForDetails.event.date)}
                                    </span>
                                </div>
                                <div className="package-datetime-item">
                                    <ClockIcon className="package-datetime-icon" />
                                    <span className="package-datetime-value">
                                        {selectedPackageForDetails.isTimeFixed ? formatTimeValue(selectedPackageForDetails.meetingTime) : 'dowolna'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="package-modal-section package-founder-section">
                            <h3 className="package-modal-section-title">Założyciel paczki</h3>
                            <div className="package-founder-row">
                                <div className="package-founder-avatar">
                                    <span>{String(selectedPackageForDetails.founder || '-').charAt(0).toUpperCase()}</span>
                                </div>
                                <div className="package-founder-info">
                                    <span className="package-founder-name">{selectedPackageForDetails.founder}</span>
                                </div>
                            </div>
                        </div>

                        <div className="package-modal-section">
                            <h3 className="package-modal-section-title">Opis paczki</h3>
                            <div className="package-description-box">
                                <p>{selectedPackageForDetails.description}</p>
                            </div>
                        </div>

                        <div className="package-modal-section">
                            <h3 className="package-modal-section-title">Paczka</h3>
                            <div className="package-members-grid">
                                {selectedPackageForDetails.users.map((user) => (
                                    <div key={user.id} className="package-member-item">
                                        <div className="package-member-avatar">
                                            <span>{String(user.username || '-').charAt(0).toUpperCase()}</span>
                                        </div>
                                        <span className="package-member-name">{user.username}</span>
                                    </div>
                                ))}
                                {selectedPackageForDetails.users.length < 8 && (
                                    Array(8 - selectedPackageForDetails.users.length).fill(null).map((_, index) => (
                                        <div key={`empty-${index}`} className="package-member-item package-member-empty">
                                            <div className="package-member-avatar empty-slot">
                                                <span>+</span>
                                            </div>
                                            <span className="package-member-name">Pusty slot</span>
                                        </div>
                                    ))
                                )}
                            </div>
                            <button className="package-show-all-btn">Pokaż wszystkich w paczce</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default UserDetailPanel;
